// Outback Station — stage decals / signage for the mural atlas (see src/world/murals.js).
//
// drawMurals(g, R, kit) draws into the canvas 2D context `g` inside R = { x, y, w, h } (2048 x 1008)
// and returns the table entries for mural ids 4..8:
//   4 shedRoof      Shearing shed bale-loft top face (10.5 x 11 m): REDGUM STATION roof stencil & bale bays
//   5 shedWall      Shearing shed yard wall (11 x 2.6 m): REDGUM PASTORAL CO. · WOOLSHED No. 2 signage
//   6 verandaDeck   Homestead veranda spawn deck (18 x 7.2 m): timber deck border & welcome stencil
//   7 borePlatform  Central Bore Windmill platform top (5.4 x 5.4 m): compass star & FLINDERS BORE No. 1 · 1896
//   8 roadSign      Station generator shed wall (3.6 x 2.2 m): Outback kangaroo diamond sign & station notice
export const MURAL_IDS = {
  shedRoof: 4,
  shedWall: 5,
  verandaDeck: 6,
  borePlatform: 7,
  roadSign: 8,
};
export const MURAL = MURAL_IDS;

const CREAM = 'rgba(246, 236, 214, 0.92)';
const OCHRE = 'rgba(198, 98, 48, 0.90)';
const RUST = 'rgba(132, 52, 28, 0.92)';
const CHARCOAL = 'rgba(38, 33, 29, 0.92)';
const GOLD = 'rgba(232, 176, 48, 0.94)';
const EUCALYPT = 'rgba(72, 102, 74, 0.88)';

function stencilBorder(g, x, y, w, h, inset, lw, color) {
  g.save();
  g.strokeStyle = color;
  g.lineWidth = lw;
  g.strokeRect(x + inset, y + inset, w - inset * 2, h - inset * 2);
  g.restore();
}

function drawCompassStar(g, cx, cy, rOuter, rInner, pts, c1, c2) {
  for (let i = 0; i < pts; i++) {
    const a0 = (i / pts) * Math.PI * 2 - Math.PI / 2;
    const aL = a0 - Math.PI / pts;
    const aR = a0 + Math.PI / pts;
    const rTip = i % 2 === 0 ? rOuter : rOuter * 0.72;
    g.fillStyle = c1;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * rTip, cy + Math.sin(a0) * rTip);
    g.lineTo(cx + Math.cos(aL) * rInner, cy + Math.sin(aL) * rInner);
    g.closePath();
    g.fill();
    g.fillStyle = c2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * rTip, cy + Math.sin(a0) * rTip);
    g.lineTo(cx + Math.cos(aR) * rInner, cy + Math.sin(aR) * rInner);
    g.closePath();
    g.fill();
  }
}

function drawKangarooIcon(g, cx, cy, s, color) {
  g.save();
  g.translate(cx, cy);
  g.scale(s, s);
  g.fillStyle = color;
  g.beginPath();
  // tail -> back -> ears -> snout -> forepaws -> hind legs
  g.moveTo(-42, 24);
  g.quadraticCurveTo(-24, 24, -10, 8);
  g.quadraticCurveTo(2, -12, 18, -22);
  g.lineTo(20, -34);
  g.lineTo(25, -24);
  g.lineTo(34, -20);
  g.lineTo(32, -14);
  g.lineTo(22, -12);
  g.lineTo(16, -2);
  g.lineTo(24, 4);
  g.lineTo(22, 8);
  g.lineTo(12, 4);
  g.quadraticCurveTo(6, 14, -2, 18);
  g.lineTo(12, 32);
  g.lineTo(2, 34);
  g.lineTo(-10, 20);
  g.quadraticCurveTo(-24, 30, -42, 28);
  g.closePath();
  g.fill();
  g.restore();
}

export function drawMurals(g, R, kit) {
  const out = [];

  // ---------------------------------------------------------------- 4: Shearing shed bale-loft top (10.5 x 11 m)
  {
    const r = { x: R.x + 8, y: R.y + 8, w: 560, h: 586 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    stencilBorder(g, r.x, r.y, r.w, r.h, 26, 8, CREAM);
    stencilBorder(g, r.x, r.y, r.w, r.h, 40, 3, OCHRE);

    // Header band
    g.fillStyle = RUST;
    g.fillRect(r.x + 52, r.y + 62, r.w - 104, 86);
    g.fillStyle = CREAM;
    g.font = kit.font(46);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('REDGUM STATION', r.x + r.w / 2, r.y + 105);

    g.fillStyle = CHARCOAL;
    g.font = kit.fontB(24);
    g.fillText('FLINDERS RANGES · S.A. · WOOLSHED LOFT', r.x + r.w / 2, r.y + 180);

    // Center stencil emblem
    const cx = r.x + r.w / 2, cy = r.y + 310;
    g.strokeStyle = CHARCOAL;
    g.lineWidth = 6;
    g.beginPath(); g.arc(cx, cy, 78, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = OCHRE;
    g.lineWidth = 3;
    g.beginPath(); g.arc(cx, cy, 66, 0, Math.PI * 2); g.stroke();
    drawCompassStar(g, cx, cy, 56, 16, 8, RUST, GOLD);

    // Bale bay floor markings
    const bayY = r.y + 430;
    g.strokeStyle = CREAM;
    g.lineWidth = 5;
    g.setLineDash([16, 10]);
    for (let i = 0; i < 3; i++) {
      const bx = r.x + 62 + i * 150;
      g.strokeRect(bx, bayY, 136, 108);
      g.fillStyle = CREAM;
      g.font = kit.fontB(22);
      g.fillText(`BALE BAY ${i + 1}`, bx + 68, bayY + 54);
    }
    g.setLineDash([]);
    g.restore();
    out.push({ id: MURAL_IDS.shedRoof, ...r, m: [10.5, 11], fx: [0.9, 1] });
  }

  // ---------------------------------------------------------------- 5: Shearing shed wall sign (11 x 1.4 m)
  {
    const r = { x: R.x + 584, y: R.y + 8, w: 880, h: 160 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    g.fillStyle = CREAM;
    g.fillRect(r.x + 24, r.y + 16, r.w - 48, r.h - 32);
    stencilBorder(g, r.x + 24, r.y + 16, r.w - 48, r.h - 32, 6, 5, CHARCOAL);
    stencilBorder(g, r.x + 24, r.y + 16, r.w - 48, r.h - 32, 13, 2, RUST);

    g.fillStyle = RUST;
    g.font = kit.font(48);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('REDGUM PASTORAL CO.', r.x + r.w / 2, r.y + 64);

    g.fillStyle = CHARCOAL;
    g.font = kit.fontB(24);
    g.fillText('WOOLSHED No. 2  ·  AAA MERINO  ·  EST. 1896', r.x + r.w / 2, r.y + 112);
    g.restore();
    out.push({ id: MURAL_IDS.shedWall, ...r, m: [11, 1.4], fx: [0.88, 1] });
  }

  // ---------------------------------------------------------------- 6: Homestead veranda deck (18 x 7.2 m)
  {
    const r = { x: R.x + 584, y: R.y + 232, w: 900, h: 360 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    stencilBorder(g, r.x, r.y, r.w, r.h, 22, 6, CREAM);
    stencilBorder(g, r.x, r.y, r.w, r.h, 34, 2, GOLD);

    // Coir welcome mat in center of veranda
    const mx = r.x + r.w / 2 - 210, my = r.y + r.h / 2 - 68, mw = 420, mh = 136;
    g.fillStyle = 'rgba(164, 114, 64, 0.88)';
    g.fillRect(mx, my, mw, mh);
    stencilBorder(g, mx, my, mw, mh, 10, 5, CHARCOAL);
    g.fillStyle = CHARCOAL;
    g.font = kit.font(36);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('REDGUM HOMESTEAD', r.x + r.w / 2, r.y + r.h / 2 - 16);
    g.fillStyle = CREAM;
    g.font = kit.fontB(22);
    g.fillText('MIND THE STEP · WIPE YOUR BOOTS', r.x + r.w / 2, r.y + r.h / 2 + 28);
    g.restore();
    out.push({ id: MURAL_IDS.verandaDeck, ...r, m: [18, 7.2], fx: [0.9, 0.85] });
  }

  // ---------------------------------------------------------------- 7: Central Bore Windmill platform (5.4 x 5.4 m)
  {
    const r = { x: R.x + 1500, y: R.y + 8, w: 520, h: 520 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2;

    // Outer octagonal / circular bore stencil ring
    g.strokeStyle = CHARCOAL;
    g.lineWidth = 14;
    g.beginPath(); g.arc(cx, cy, 220, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = CREAM;
    g.lineWidth = 8;
    g.beginPath(); g.arc(cx, cy, 202, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = GOLD;
    g.lineWidth = 5;
    g.setLineDash([18, 12]);
    g.beginPath(); g.arc(cx, cy, 184, 0, Math.PI * 2); g.stroke();
    g.setLineDash([]);

    drawCompassStar(g, cx, cy, 168, 42, 16, RUST, CREAM);
    drawCompassStar(g, cx, cy, 110, 28, 8, CHARCOAL, GOLD);

    g.fillStyle = CHARCOAL;
    g.beginPath(); g.arc(cx, cy, 54, 0, Math.PI * 2); g.fill();
    g.strokeStyle = GOLD;
    g.lineWidth = 4;
    g.beginPath(); g.arc(cx, cy, 48, 0, Math.PI * 2); g.stroke();

    g.fillStyle = CREAM;
    g.font = kit.fontB(20);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('FLINDERS BORE', cx, cy - 11);
    g.fillStyle = GOLD;
    g.font = kit.fontB(18);
    g.fillText('No. 1 · 1896', cx, cy + 13);
    g.restore();
    out.push({ id: MURAL_IDS.borePlatform, ...r, m: [5.4, 5.4], fx: [0.92, 0.8] });
  }

  // ---------------------------------------------------------------- 8: Outback road sign & station notice (3.6 x 2.2 m)
  {
    const r = { x: R.x + 8, y: R.y + 612, w: 540, h: 330 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();

    // Left: Aussie yellow diamond kangaroo sign
    const dx = r.x + 145, dy = r.y + 165, dr = 108;
    g.fillStyle = GOLD;
    g.beginPath();
    g.moveTo(dx, dy - dr); g.lineTo(dx + dr, dy); g.lineTo(dx, dy + dr); g.lineTo(dx - dr, dy);
    g.closePath();
    g.fill();
    g.strokeStyle = CHARCOAL;
    g.lineWidth = 7;
    g.stroke();
    drawKangarooIcon(g, dx, dy + 4, 1.45, CHARCOAL);

    // Right: Station Fuel & Water notice board
    const bx = r.x + 275, by = r.y + 52, bw = 240, bh = 226;
    g.fillStyle = CREAM;
    g.fillRect(bx, by, bw, bh);
    stencilBorder(g, bx, by, bw, bh, 8, 5, CHARCOAL);
    g.fillStyle = EUCALYPT;
    g.fillRect(bx + 16, by + 18, bw - 32, 44);
    g.fillStyle = CREAM;
    g.font = kit.font(26);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('OUTBACK TRACK', bx + bw / 2, by + 40);

    g.fillStyle = CHARCOAL;
    g.font = kit.fontB(20);
    g.fillText('CHECK WATER', bx + bw / 2, by + 95);
    g.fillText('BEFORE CROSSING', bx + bw / 2, by + 122);
    g.fillStyle = RUST;
    g.font = kit.font(24);
    g.fillText('CREEK FLOODWAY', bx + bw / 2, by + 162);
    g.fillStyle = CHARCOAL;
    g.font = kit.fontB(18);
    g.fillText('DIESEL · BORE TANK', bx + bw / 2, by + 198);
    g.restore();
    out.push({ id: MURAL_IDS.roadSign, ...r, m: [3.6, 2.2], fx: [0.85, 1] });
  }

  return out;
}
